import type {
  AnalyticsEventMap,
  AnalyticsEventName,
  AnalyticsProvider,
  AnalyticsUser,
} from './types'
import { posthog } from 'posthog-js'

import { PostHogAnalyticsProvider } from './posthog-adapter'

const noopAnalyticsProvider: AnalyticsProvider = {
  identify() {},
  reset() {},
  track() {},
}

let activeAnalyticsProvider: AnalyticsProvider = noopAnalyticsProvider
let analyticsInitialized = false
let analyticsConsentGranted = false

function isEmbeddedRuntime() {
  return typeof window !== 'undefined' && window.parent !== window
}

export function initAnalytics() {
  if (typeof window === 'undefined') {
    return noopAnalyticsProvider
  }

  if (analyticsInitialized) {
    return activeAnalyticsProvider
  }

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
  if (!key) {
    activeAnalyticsProvider = noopAnalyticsProvider
    return activeAnalyticsProvider
  }

  const embeddedRuntime = isEmbeddedRuntime()

  posthog.init(key, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    capture_pageview: !embeddedRuntime,
    disable_session_recording: embeddedRuntime,
    mask_all_element_attributes: embeddedRuntime,
    mask_all_text: embeddedRuntime,
    opt_out_capturing_by_default: embeddedRuntime,
    person_profiles: 'identified_only',
  })

  analyticsInitialized = true
  analyticsConsentGranted = !embeddedRuntime
  activeAnalyticsProvider = new PostHogAnalyticsProvider()
  return activeAnalyticsProvider
}

function ensureAnalyticsInitialized() {
  if (!analyticsInitialized) {
    initAnalytics()
  }

  return analyticsInitialized
}

function enableAnalyticsCapture() {
  if (!ensureAnalyticsInitialized() || analyticsConsentGranted) {
    return
  }

  posthog.opt_in_capturing({ captureEventName: false })

  if (isEmbeddedRuntime()) {
    posthog.startSessionRecording()
    posthog.capture('$pageview')
  }

  analyticsConsentGranted = true
}

function disableAnalyticsCapture() {
  if (!ensureAnalyticsInitialized()) {
    return
  }

  posthog.stopSessionRecording()
  posthog.opt_out_capturing()
  posthog.reset()
  analyticsConsentGranted = false
}

export function setAnalyticsConsent(hasConsent: boolean) {
  if (hasConsent) {
    enableAnalyticsCapture()
    return
  }

  disableAnalyticsCapture()
}

export function identifyUser(user: AnalyticsUser) {
  activeAnalyticsProvider.identify(user)
}

export function resetAnalytics(resetDeviceId?: boolean) {
  activeAnalyticsProvider.reset(resetDeviceId)
}

export function trackEvent<EventName extends AnalyticsEventName>(
  event: EventName,
  properties: AnalyticsEventMap[EventName],
) {
  activeAnalyticsProvider.track(event, properties)
}
